import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Input,
  Layout,
  Modal,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import {
  ToolOutlined,
  ExclamationCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons'
import moment from 'moment'
import serviceInterface from '../../services/service.interface'

const { Content } = Layout
const { Text, Title } = Typography

const ACTIVE_WINDOW_MINUTES = 5
const ACTIVE_USERS_REFRESH_MS = 30 * 1000

// SUPER_ADMIN-only page: global maintenance-mode toggle plus a live view of
// who has been active in the last few minutes (i.e. who a toggle would kick).
function SystemMaintenance() {
  const maintenanceService = useRef(serviceInterface.MaintenanceService())
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [state, setState] = useState({
    enabled: false,
    message: null,
    startedAt: null,
    endedAt: null,
  })
  const [customMessage, setCustomMessage] = useState('')
  const [activeUsers, setActiveUsers] = useState([])
  const [activeTotal, setActiveTotal] = useState(0)
  const [usersLoading, setUsersLoading] = useState(false)

  const loadState = useCallback(() => {
    return maintenanceService.current
      .getMaintenanceMode()
      .then((res) => {
        const data = (res && res.data) || {}
        setState({
          enabled: !!data.enabled,
          message: data.message || null,
          startedAt: data.startedAt || null,
          endedAt: data.endedAt || null,
        })
        setCustomMessage(data.message || '')
      })
      .catch(() => message.error('Failed to load maintenance state'))
  }, [])

  const loadActiveUsers = useCallback(() => {
    setUsersLoading(true)
    return maintenanceService.current
      .getActiveUsers({ windowMinutes: ACTIVE_WINDOW_MINUTES })
      .then((res) => {
        setActiveUsers((res && res.data) || [])
        setActiveTotal((res && res.total) || 0)
      })
      .catch(() => {})
      .finally(() => setUsersLoading(false))
  }, [])

  useEffect(() => {
    Promise.all([loadState(), loadActiveUsers()]).finally(() =>
      setLoading(false)
    )
    const timer = setInterval(loadActiveUsers, ACTIVE_USERS_REFRESH_MS)
    return () => clearInterval(timer)
  }, [loadState, loadActiveUsers])

  const applyChange = (enabled) => {
    setSaving(true)
    maintenanceService.current
      .setMaintenanceMode({
        enabled,
        message: customMessage.trim() || null,
      })
      .then((res) => {
        const data = (res && res.data) || {}
        setState({
          enabled: !!data.enabled,
          message: data.message || null,
          startedAt: data.startedAt || null,
          endedAt: data.endedAt || null,
        })
        message.success(
          enabled
            ? 'Maintenance mode enabled — active sessions are being disconnected.'
            : 'Maintenance mode disabled — logins are open again.'
        )
        loadActiveUsers()
      })
      .catch(() => message.error('Failed to update maintenance mode'))
      .finally(() => setSaving(false))
  }

  const confirmEnable = () => {
    Modal.confirm({
      title: 'Enable maintenance mode?',
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <p>
            <b>{activeTotal}</b> user{activeTotal === 1 ? '' : 's'} ha
            {activeTotal === 1 ? 's' : 've'} been active in the last{' '}
            {ACTIVE_WINDOW_MINUTES} minutes and will be disconnected
            immediately.
          </p>
          <p>
            New logins will be blocked until you turn this off. Super admins
            can still log in.
          </p>
        </div>
      ),
      okText: 'Enable maintenance',
      okButtonProps: { danger: true },
      onOk: () => applyChange(true),
    })
  }

  const confirmDisable = () => {
    Modal.confirm({
      title: 'End maintenance mode?',
      icon: <ExclamationCircleOutlined />,
      content:
        'Logins reopen immediately. Every session from before the maintenance window stays invalidated — all users will log in again.',
      okText: 'End maintenance',
      onOk: () => applyChange(false),
    })
  }

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Type',
      dataIndex: 'usertype',
      key: 'usertype',
      render: (usertype) => (
        <Tag color={usertype === 'Patient' ? 'blue' : 'green'}>{usertype}</Tag>
      ),
    },
    {
      title: 'Clinic',
      dataIndex: 'clinic',
      key: 'clinic',
      render: (clinic) => clinic || '—',
    },
    {
      title: 'Last activity',
      dataIndex: 'lastActiveAt',
      key: 'lastActiveAt',
      render: (t) => (t ? moment(t).fromNow() : '—'),
    },
  ]

  if (loading) {
    return (
      <Content style={{ padding: 24, textAlign: 'center' }}>
        <Spin />
      </Content>
    )
  }

  return (
    <Content style={{ padding: 24, maxWidth: 900 }}>
      <Title level={3}>
        <ToolOutlined /> System Maintenance
      </Title>

      {state.enabled ? (
        <Alert
          type='warning'
          showIcon
          style={{ marginBottom: 16 }}
          message='Maintenance mode is ON'
          description={
            <span>
              Since {moment(state.startedAt).format('lll')} (
              {moment(state.startedAt).fromNow()}). All logins and requests are
              blocked except super admins.
              {state.message ? (
                <div>
                  Banner message: <Text italic>“{state.message}”</Text>
                </div>
              ) : null}
            </span>
          }
        />
      ) : (
        <Alert
          type='success'
          showIcon
          style={{ marginBottom: 16 }}
          message='Maintenance mode is off'
          description={
            state.endedAt
              ? `Last maintenance window ended ${moment(state.endedAt).fromNow()}.`
              : 'No maintenance window has been run yet.'
          }
        />
      )}

      <Card title='Maintenance switch' style={{ marginBottom: 16 }}>
        <Space direction='vertical' style={{ width: '100%' }}>
          <Text>
            Custom banner message (optional — shown on both login screens; the
            default copy is used when empty):
          </Text>
          <Input.TextArea
            rows={2}
            maxLength={300}
            placeholder='e.g. Scheduled maintenance — back at 10pm ET.'
            value={customMessage}
            onChange={(e) => setCustomMessage(e.target.value)}
          />
          {state.enabled ? (
            <Space>
              <Button type='primary' loading={saving} onClick={confirmDisable}>
                End maintenance
              </Button>
              <Button
                loading={saving}
                onClick={() => applyChange(true)}
                title='Save an updated banner message without restarting the window'
              >
                Update message
              </Button>
            </Space>
          ) : (
            <Button
              type='primary'
              danger
              loading={saving}
              onClick={confirmEnable}
            >
              Enable maintenance mode
            </Button>
          )}
          <Text type='secondary'>
            Enabling disconnects every active session (websockets included),
            blocks all requests and logins, and permanently invalidates
            pre-maintenance sessions — everyone logs in again afterwards. Super
            admins are exempt so this page stays reachable.
          </Text>
        </Space>
      </Card>

      <Card
        title={`Active in the last ${ACTIVE_WINDOW_MINUTES} minutes (${activeTotal})`}
        extra={
          <Button
            size='small'
            icon={<ReloadOutlined />}
            loading={usersLoading}
            onClick={loadActiveUsers}
          >
            Refresh
          </Button>
        }
      >
        <Table
          size='small'
          rowKey='_id'
          columns={columns}
          dataSource={activeUsers}
          loading={usersLoading}
          pagination={{ pageSize: 10, hideOnSinglePage: true }}
          locale={{ emptyText: 'No recent activity' }}
        />
        <Text type='secondary'>
          Counts accounts whose app talked to the server recently (refreshes
          every 30s, heartbeat throttled to once a minute per account) — an
          idle-but-open app can show as inactive.
        </Text>
      </Card>
    </Content>
  )
}

export default SystemMaintenance
